import { startIntegrationOAuth } from '@/lib/open-integration-oauth';
import { resolveApiUrl } from '@/lib/resolve-api-url';
import { useCallback, useMemo } from 'react';

interface UseInitiateMetaAdsAuthOptions {
  /** URL to redirect back to after OAuth completes */
  returnTo?: string;
}

/**
 * Hook to initiate Meta Ads (Facebook) OAuth flow
 *
 * @example
 * ```tsx
 * const { initiateAuth, authUrl } = useInitiateMetaAdsAuth({
 *   returnTo: '/integrations'
 * });
 *
 * <button onClick={initiateAuth}>Connect Meta Ads</button>
 * ```
 */
export function useInitiateMetaAdsAuth(
  options: UseInitiateMetaAdsAuthOptions = {}
) {
  const { returnTo } = options;

  const authUrl = useMemo(
    () =>
      returnTo
        ? resolveApiUrl(
            `integrations/meta-ads/auth?returnTo=${encodeURIComponent(returnTo)}`
          )
        : resolveApiUrl('integrations/meta-ads/auth'),
    [returnTo]
  );

  /**
   * Initiate the OAuth flow.
   */
  // Fetched with the Bearer attached, then followed — a full-page navigation to
  // the guarded `meta-ads/auth` endpoint can carry only cookies, and this app's
  // session cookie expires long before its Bearer token does.
  //
  // `returnTo` is deliberately not forwarded: the Meta Ads flow does not sign it
  // into the OAuth state (see `startMetaAdsConnect`), so the redirect endpoint
  // ignores it too. `authUrl` below still carries it for href parity.
  const initiateAuth = useCallback(() => {
    void startIntegrationOAuth('integrations/meta-ads/authorize-url');
  }, []);

  return {
    /** The full auth URL for the OAuth flow */
    authUrl,
    /** Function to initiate the OAuth redirect */
    initiateAuth,
  };
}
